<template>
	<div class="subscription-card" :class="{ 'subscription-card--inactive': is_inactive_quote_line }">
		<div class="subscription-card-header">
			<h6 class="subscription-card-title">{{ item.product_name || item.product_id }}</h6>
			<span v-if="!quote && item.promotion_badge" class="eshop-promo-badge" :style="item.promotion_badge.style"
				:lang="item.promotion_badge.title_lang || undefined">{{ item.promotion_badge.title }}</span>
			<span v-if="status_badge" class="subscription-card-badge">{{ status_badge }}</span>
			<span v-if="!qty_is_editable" class="subscription-card-qty-chip">{{ item_qty }}</span>
			<div v-if="!readonly && item_can_be_removed()" class="del grey-btn" @click="emit('removeItem', item)">
				<svg class="icon sm remove-cart-item-logo" width="18" height="18"
					viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" id="icon-close">
					<path fill-rule="evenodd" clip-rule="evenodd"
						d="M4.146 11.217a.5.5 0 1 0 .708.708l3.182-3.182 3.181 3.182a.5.5 0 1 0 .708-.708l-3.182-3.18 3.182-3.182a.5.5 0 1 0-.708-.708l-3.18 3.181-3.183-3.182a.5.5 0 0 0-.708.708l3.182 3.182-3.182 3.181z"
						stroke-width="0"></path>
				</svg>
			</div>
		</div>

		<div v-if="best_max_user_not_achieved" class="text-danger small subscription-card-warning">
			<div>{{ __("This product may be unsuitable for the number of user defined.") }}</div>
			<div>{{ __("Please check the product description.") }}</div>
		</div>

		<div class="subscription-card-meta">
			<span class="subscription-card-chip">{{ __(item_unit) }}</span>
			<span v-if="item.pricing_unit" class="subscription-card-chip">
				{{ __(item.pricing_unit) }}
			</span>
			<span v-if="item.estimated_setup_time" class="subscription-card-chip">{{ __("Est. setup time") }}: {{ item.estimated_setup_time }} {{ __("H") }}</span>
			<span v-if="is_one_time_purchase" class="subscription-card-chip subscription-card-chip--purchase">{{ __("Purchase") }}</span>
		</div>

		<div v-if="evaluation_note && item.trial_requested" class="small text-muted mt-2 subscription-card-evaluation">{{ __("Evaluation mode, not effective order") }}</div>

		<div v-if="qty_is_editable" class="subscription-card-qty-row">
			<div class="qty-minus grey-btn" @click="emit('minusQty', item)"><span>-</span></div>
			<div class="qty">{{ item_qty }}</div>
			<div class="qty-plus grey-btn" @click="emit('plusQty', item)"><span>+</span></div>
		</div>

		<div v-if="boost_price_hint" class="subscription-card-boost-hint">{{ boost_price_hint }}</div>

		<div class="subscription-card-divider"></div>

		<dl v-if="!quote" class="subscription-card-dates">
			<div>
				<dt>{{ __("Renewal Date") }}</dt>
				<dd>{{ __(renewal_date) }}</dd>
			</div>
		</dl>

		<dl class="subscription-card-price">
			<div>
				<dt>{{ __("Setup and Purchase") }}<price-rule-trigger v-if="show_variable_price_marker && !item.is_recurring" :rules="price_rules" /></dt>
				<dd :class="amount_class(install_cost)">{{ ioi_format_currency(install_cost || 0.0, item.currency) }}</dd>
			</div>
			<div>
				<dt>{{ __("Unit Subscription") }}</dt>
				<dd :class="amount_class(enduser_price)">{{ ioi_format_currency(enduser_price || 0.0, item.currency) }}</dd>
			</div>
			<div class="subscription-card-total">
				<dt>{{ __("Total Subscription") }}<price-rule-trigger v-if="show_variable_price_marker && item.is_recurring" :rules="price_rules" /></dt>
				<dd :class="[amount_class(item_total), is_eval ? 'linethrough' : '']">{{ ioi_format_currency(item_total || 0.0, item.currency) }}</dd>
			</div>
		</dl>
	</div>
</template>


<script setup>
import { ioi_format_currency } from "./format_currency";
import { BOOST_PRODUCT_ID } from "./constants";
import PriceRuleTrigger from "./PriceRuleTrigger.vue";
import { computed} from 'vue'

const props = defineProps({
	item: {
		type: Object,
		default: () => { return {} }
	},
	users: {
		type: Number,
		default: 0
	},
	// Read-only columns (Active subscriptions / In progress) never render qty or
	// remove controls, not even as disabled/hidden glyphs - see the audit note on
	// item_can_be_updated()/item_can_be_removed() only hiding glyphs today.
	readonly: {
		type: Boolean,
		default: false
	},
	// A quote's line (QuotePage.vue), not a subscription's: its quantity is qty_in_quote whatever its
	// status (Active, Inactive, OutDated: the quote's own), it has no renewal date, and its badge is
	// "Quoted Qty", or "Inactive" for a line Desk took out of the quote. Off by default: every other card
	// is as before.
	quote: {
		type: Boolean,
		default: false
	},
	// The mention of a line ordered for evaluation (trial_requested), under its chips: asked for by the In
	// progress and Active columns only (SubscriptionColumn.vue), never by the Cart's or a quote's cards.
	// Off by default: every other card is as before.
	evaluation_note: {
		type: Boolean,
		default: false
	},
	// The "**" of a variable line (its stored variable_price) after its price, opening its product's rule
	// (PriceRuleTrigger.vue): after "Total Subscription", or "Setup and Purchase" for a line that is not
	// recurring. Asked for by the In progress column (SubscriptionColumn.vue) and a quote's page only. Off
	// by default: every other card is as before.
	variable_price_marker: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['minusQty', 'plusQty', 'removeItem'])

const item_qty = computed(() => {
	if (props.quote) {
		return props.item.qty_in_quote
	}
	if (["Inactive", "Active", "Locked"].includes(props.item.status)) {
		return props.item.active_qty
	} else if (props.item.status == "Under Process") {
		return props.item.qty_ordered
	} else if (props.item.status == "In Basket") {
		return props.item.qty_in_basket
	}
})

const item_total= computed(() => {
	if (["One of", "Credit"].includes(props.item.unit_of_measure)) {
		return 0.0
	}

	return subscription_total.value
})

const subscription_total = computed(() => {
	if (props.quote) {
		return props.item.qty_in_quote * props.item.enduser_price
	}
	if (["Inactive", "Active", "Locked"].includes(props.item.status)) {
		return props.item.active_qty * props.item.enduser_price
	} else if (props.item.status == "Under Process") {
		return props.item.qty_ordered * props.item.enduser_price
	} else if (props.item.status == "In Basket") {
		return props.item.qty_in_basket * props.item.enduser_price
	}
})

const item_unit = computed(() => {
	return ["One of", "Credit"].includes(props.item.unit_of_measure) ? "Unit" : props.item.unit_of_measure
})

// Single source of truth for "does this card show the stepper or the header
// chip" - keeps the two mutually exclusive instead of drifting apart.
const qty_is_editable = computed(() => !props.readonly && item_can_be_updated())

const is_eval = computed(() => {
	return props.item.status == "Under Process" && props.item.trial_requested
})

const enduser_price = computed(() => {
	return ["One of", "Credit"].includes(props.item.unit_of_measure) ? 0.0 : props.item.enduser_price
})

const install_cost = computed(() => {
	// flt is an ambient global from frappe-web.bundle.js (frappe/utils/number_format.js);
	// no ES module export exists for it in this bench, so it is qualified explicitly.
	return ["One of", "Credit"].includes(props.item.unit_of_measure) ? subscription_total.value + window.flt(props.item.install_cost) : props.item.install_cost
})

const renewal_date = computed(() => {
	return props.item.renewal_date ? frappe.datetime.obj_to_user(props.item.renewal_date) : "Not renewable"
})

// Per-card state badge for the merged in-progress column (Inactive + Under Process).
// Never surface the raw status value - everything else (Active, In Basket, Locked,
// Archive) shows no badge.
const status_badge = computed(() => {
	if (props.quote) {
		return is_inactive_quote_line.value ? __("Inactive") : __("Quoted Qty")
	}
	if (props.item.status == "Inactive") {
		return __("Ready to start")
	} else if (props.item.status == "Under Process") {
		return __("Order received")
	}
	return null
})

// A one-time line the customer owns outright rather than rents monthly - the one
// thing the recurring rows around it cannot say for themselves.
//
// "Purchase", not "Paid": whether an invoice has actually been settled is an
// accounting fact this app never sees, so asserting it here would be a claim the
// e-shop cannot stand behind. "Purchase" describes the nature of the line instead,
// which stays true in every status it can reach.
//
// Classified strictly on the is_recurring key resolved from the Pricing Unit
// (with_product_names), never on unit_of_measure - that field's stored casing is
// "One Of", which the comparisons elsewhere in this file do not match (separate
// ticket).
//
// Deliberately excludes In Basket (not bought yet) and Under Process (ordered,
// not yet delivered) - those already carry their own status badge.
const is_one_time_purchase = computed(() => {
	return !props.quote && props.item.is_recurring === false && ["Active", "Inactive"].includes(props.item.status)
})

const show_variable_price_marker = computed(() => props.variable_price_marker && !!props.item.variable_price)

// The line's rule for the bubble, from the server (with_product_names, get_quote_details): none when its
// product has no text, and the "**" is then plain text.
const price_rules = computed(() => {
	return props.item.price_rule ? [{ html: props.item.price_rule, lang: props.item.price_rule_lang }] : []
})

// A quote's line Desk took out of the quote: greyed, and left out of the quote's totals and of what Push
// to basket puts in the basket (quotes.py).
const is_inactive_quote_line = computed(() => props.quote && props.item.status == "Inactive")

const boost_price_hint = computed(() => {
	if (props.item.product_id !== BOOST_PRODUCT_ID) {
		return null
	}
	return __("+ {0} / user", [ioi_format_currency(props.item.enduser_price || 0.0, props.item.currency)])
})

const best_max_user_not_achieved = computed(() => {
	if (!props.item.best_max_user) {
		return false
	}

	return props.item.best_max_user > props.users
})

function item_can_be_updated() {
	// Active/Inactive lines are frozen: they are committed, and reducing or stopping
	// an ordered product is a commercial process handled by accounting, never a
	// button in the e-shop. Only an In Basket line can still be adjusted here.
	return (
		props.item.status == 'In Basket'
		&& (["User", "Credit"].includes(props.item.unit_of_measure) || props.item.allow_quantity)
	)
}

function item_can_be_removed() {
	return props.item.status == 'In Basket'
}

// Compares the raw numeric field, not the formatted string - formatting is
// locale-dependent (ioi_format_currency) and not safe to test for "zero".
function amount_class(value) {
	return (value || 0) === 0 ? '' : 'amount-bold'
}

</script>

<style scoped lang="scss">
// .qty-minus/.qty/.qty-plus/.del now live in ioi_partners_eshop.bundle.scss under
// body.product-page, so this card's stepper/remove control can be styled consistently
// with the rest of the column without duplicating the same rules in two files.
.linethrough {
	text-decoration: line-through;
}

.subscription-card--inactive {
	opacity: .55;
}
</style>